import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import { Dialog, Portal, CloseButton, Button, Table, Input, Group, For, Checkbox, Box, Text, VStack, HStack, RadioGroup } from "@chakra-ui/react";

import { LuFocus, LuPen, LuTrash } from "react-icons/lu";
import { Color, JulianDate, Math as CesiumMath, Cartographic } from "cesium";

import { getCurrentViewBoundingBox, getGeojsonPropertiesKey, moveCamaraView, onMapMove, removeFileExtension } from "../../../../../utilies/Utility";

import { getEntityPlainProps, collectCandidateIds, removeFromFeatureCollectionByAnyId, updateLayerAndMapObjectsGeoJSON } from "../../../../../utilies/GeojsonUtility";

import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import Type from "../../../../../utilies/Types";
import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";

const LayersAttributeTable = () => {
  // state
  const showQueryTableDialog = useAppStore((s) => s.showQueryTableDialog);
  const queryTable = useAppStore((s) => s.queryTable);
  const selectedFeatures = useAppStore((s) => s.selectedFeatures);
  const userType = useAppStore((s) => s.userType);
  const currentLayerForAttributeTable = useAppStore((s) => s.currentLayerForAttributeTable);

  // setters / refs
  const setShowQueryTableDialog = useAppStore.getState().setShowQueryTableDialog;
  const setQueryTable = useAppStore.getState().setQueryTable;
  const viewerRef = useAppStore.getState().viewerRef;
  const setEditFeatureDialog = useAppStore.getState().setEditFeatureDialog;
  const setSelectedFeatures = useAppStore.getState().setSelectedFeatures;
  const setBultEditDialog = useAppStore.getState().setBultEditDialog;
  const { setMapObjects } = useMapObjects();
  const setCurrentLayerForAttributeTable = useAppStore.getState().setCurrentLayerForAttributeTable;

  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [currentViewBounds, setCurrentViewBounds] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [cols, setCols] = useState([]);
  const [selectedVisibilityMode, setSelectedVisibilityMode] = useState("0");

  const selectedSet = useMemo(() => new Set(selectedFeatures), [selectedFeatures]);

  const normalizeCellValue = useCallback((val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return val;

    if (val instanceof JulianDate) return JulianDate.toDate(val).toISOString();

    if (typeof Color !== "undefined" && val instanceof Color) return val.toCssColorString();

    if (typeof val === "object" && "x" in val && "y" in val) {
      return `${val.x}, ${val.y}${"z" in val ? ", " + val.z : ""}`;
    }

    if (typeof val === "object" && "_value" in val && "_definitionChanged" in val) {
      return normalizeCellValue(val._value);
    }

    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }, []);

  const applyVisibility = useCallback((viewer, dataSource, entityList, visibilityMode) => {
    if (!viewer || !dataSource || !dataSource.entities) return;

    const allEntities = dataSource.entities.values;
    const filteredIdSet = new Set(entityList.map((e) => e.id));

    for (const entity of allEntities) {
      switch (visibilityMode) {
        case "0":
          entity.show = true;
          break;
        case "1":
          entity.show = !filteredIdSet.has(entity.id);
          break;
        case "2":
          entity.show = filteredIdSet.has(entity.id);
          break;
        default:
          entity.show = true;
      }
    }
  }, []);

  const filterFeature = useCallback(
    (bounds) => {
      const layer = currentLayerForAttributeTable;
      if (!layer?.dataSource) {
        setQueryTable([]);
        return;
      }

      const nextCols = getGeojsonPropertiesKey(getEditedGeoJSON(layer));
      setCols((prev) => {
        if (prev.length === nextCols.length && prev.every((v, i) => v === nextCols[i])) return prev;
        return nextCols;
      });

      const dataSource = layer.dataSource;
      const exprRaw = (filterSearch || "").trim();

      if (!bounds) {
        setQueryTable([]);
        return;
      }

      const containsComparisonOrLogic = (() => {
        const opRegex = /==|!=|<=|>=|<|>|\b(or|and|not)\b/i;
        if (opRegex.test(exprRaw)) return true;
        const hasEq = exprRaw.includes("=");
        if (!hasEq) return false;
        if (!exprRaw.includes("==") && !exprRaw.includes("!=") && !exprRaw.includes("<=") && !exprRaw.includes(">=")) return true;
        return false;
      })();

      let predicate = null;
      let useFreeText = false;

      if (exprRaw.length > 0) {
        if (containsComparisonOrLogic) {
          let jsExpr = exprRaw
            .replace(/\bor\b/gi, "||")
            .replace(/\band\b/gi, "&&")
            .replace(/\bnot\b/gi, "!");
          const hasSingleEqual = exprRaw.includes("=") && !exprRaw.includes("==") && !exprRaw.includes("!=") && !exprRaw.includes("<=") && !exprRaw.includes(">=");
          if (hasSingleEqual) jsExpr = jsExpr.replace(/=/g, "==");

          try {
            predicate = new Function("props", "bbox", "with(props){ return (" + jsExpr + "); }");
          } catch (e) {
            console.error("Invalid filter expression:", e);
            setQueryTable([]);
            return;
          }
        } else {
          useFreeText = true;
        }
      }

      const entities = dataSource.entities.values;
      const temp = [];

      const isPositionInsideBounds = (position) => {
        if (!position) return false;
        const carto = Cartographic.fromCartesian(position);
        const lat = CesiumMath.toDegrees(carto.latitude);
        const lon = CesiumMath.toDegrees(carto.longitude);
        return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
      };

      const q = exprRaw.toLowerCase();
      const entityList = [];

      for (const entity of entities) {
        if (temp.length >= 200) break;

        const properties = entity.properties;
        if (!properties) continue;

        const propsObj = {};
        for (let key of nextCols) {
          const raw = properties[key]?.getValue?.() ?? properties[key] ?? "";
          propsObj[key] = raw;
        }

        propsObj.id = propsObj.id ?? entity.id ?? propsObj.ID ?? propsObj.Id;
        propsObj._entityId = entity.id;

        let positions = null;
        if (entity.polyline?.positions) {
          positions = entity.polyline.positions.getValue(JulianDate.now());
        } else if (entity.polygon?.hierarchy?.getValue) {
          const hierarchy = entity.polygon.hierarchy.getValue(JulianDate.now());
          if (hierarchy?.positions) positions = hierarchy.positions;
        } else if (entity.position?.getValue) {
          positions = [entity.position.getValue(JulianDate.now())];
        }

        if (!positions || !positions.some(isPositionInsideBounds)) continue;

        let matched = true;
        if (predicate) {
          try {
            matched = !!predicate(propsObj, bounds);
          } catch (e) {
            console.warn("Filter eval error for entity:", e);
            matched = false;
          }
        } else if (useFreeText) {
          if (q.length === 0) {
            matched = true;
          } else {
            let found = false;
            for (let key of nextCols) {
              const val = propsObj[key];
              if (val === null || val === undefined) continue;
              if (typeof val === "object") continue;
              const s = String(val).toLowerCase();
              if (s.includes(q)) {
                found = true;
                break;
              }
            }
            matched = found;
          }
        } else {
          matched = true;
        }

        if (!matched) continue;

        const rowId = propsObj.id ?? entity.id ?? propsObj.ID ?? propsObj.Id;

        const row = { entity, id: rowId };
        nextCols.forEach((key) => {
          row[key] = normalizeCellValue(propsObj[key]);
        });

        temp.push(row);
        entityList.push(entity);
      }

      applyVisibility(viewerRef.current, layer.dataSource, entityList, selectedVisibilityMode);
      setQueryTable(temp);
    },
    [currentLayerForAttributeTable, filterSearch, normalizeCellValue, applyVisibility, selectedVisibilityMode, setQueryTable, viewerRef]
  );

  const setBboxAndFilter = useCallback(() => {
    if (!showQueryTableDialog) {
      setQueryTable([]);
      setFilterSearch("");
      return;
    }
    const bounds = getCurrentViewBoundingBox(viewerRef.current);
    setCurrentViewBounds(bounds);
    filterFeature(bounds);
  }, [showQueryTableDialog, filterFeature, setQueryTable, viewerRef]);

  // Register map move handler
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const off = onMapMove(viewer, () => {
      setBboxAndFilter();
    });

    return () => {
      if (typeof off === "function") off();
    };
  }, [viewerRef, setBboxAndFilter]);

  useEffect(() => {
    if (showQueryTableDialog) setBboxAndFilter();
  }, [showQueryTableDialog, setBboxAndFilter]);

  useEffect(() => {
    if (queryTable.length > 0) {
      const allSelected = queryTable.every((item) => selectedSet.has(item.id));
      setSelectAllChecked(allSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [selectedSet, queryTable]);

  useEffect(() => {
    try {
      filterFeature(currentViewBounds);
    } catch {}
  }, [selectedVisibilityMode]);

  const handleDeleteFeature = (row, currentLayerForAttributeTable, setQueryTable, setSelectedFeatures, setCurrentLayerForAttributeTable, setMapObjects) => {
    if (!row?.entity || !currentLayerForAttributeTable?.dataSource) return;

    const { entity, id } = row;
    const ds = currentLayerForAttributeTable.dataSource;
    const layerId = currentLayerForAttributeTable.id;

    if (ds && layerId && !ds.layerId) ds.layerId = layerId;

    try {
      ds.entities.remove(entity);
    } catch {
      if (entity.id) ds.entities.removeById(entity.id);
    }

    setQueryTable((prev) => prev.filter((r) => r.id !== id));
    setSelectedFeatures((prev) => prev.filter((x) => x !== id));

    try {
      const propsNow = getEntityPlainProps(entity);
      const candidates = collectCandidateIds(propsNow);

      if (currentLayerForAttributeTable.geojson) {
        const nextGeoJSON = removeFromFeatureCollectionByAnyId(currentLayerForAttributeTable.geojson, candidates);
        updateLayerAndMapObjectsGeoJSON(currentLayerForAttributeTable.id, nextGeoJSON, setCurrentLayerForAttributeTable, setMapObjects);
      }

      if (layerId) {
        setMapObjects((prev) => prev.map((obj) => (obj.id === layerId ? { ...obj, is_edited: true } : obj)));
      }
    } catch {}
  };

  // --------- Virtualized rows ----------
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(420);

  const ROW_H = 34;
  const OVERSCAN = 10;

  const onTableScroll = useCallback((e) => {
    const top = e.currentTarget.scrollTop;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setScrollTop(top);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      setViewportH(el.clientHeight || 420);
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const total = queryTable.length;
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(total, Math.ceil((scrollTop + viewportH) / ROW_H) + OVERSCAN);
  const topPad = start * ROW_H;
  const bottomPad = (total - end) * ROW_H;

  const visibleRows = useMemo(() => queryTable.slice(start, end), [queryTable, start, end]);

  const canEdit = userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN);

  const visibilityItems = useMemo(
    () => [
      { label: "Show All", value: "0" },
      { label: "Hide Filtered Features", value: "1" },
      { label: "Hide Unfiltered Features", value: "2" },
    ],
    []
  );

  const Row = useMemo(() => {
    const MemoRow = React.memo(function MemoRow({ item }) {
      const isChecked = selectAllChecked || selectedSet.has(item.id);

      return (
        <Table.Row height={`${ROW_H}px`}>
          <Table.Cell textAlign="end" py="0" height={`${ROW_H}px`}>
            <HStack gap={0} height="100%">
              {canEdit ? (
                <Checkbox.Root
                  py={0}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    const next = checked.checked;
                    setSelectedFeatures((prev) => {
                      if (next) {
                        if (prev.includes(item.id)) return prev;
                        return [...prev, item.id];
                      }
                      return prev.filter((id) => id !== item.id);
                    });
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control cursor="pointer" />
                </Checkbox.Root>
              ) : null}

              <Button
                size="sm"
                p={0}
                m={0}
                variant="outline"
                colorScheme="blue"
                outline="none"
                border="none"
                onClick={() => {
                  const entity = item.entity;

                  moveCamaraView(viewerRef.current, { type: "entity", entity });

                  const originalMaterial = entity.polygon?.material;
                  const originalColor = entity.point?.color;
                  const originalPolylineMaterial = entity.polyline?.material;
                  const originalWidth = entity.polyline?.width?.getValue?.() ?? entity.polyline?.width;

                  let isOn = false;
                  const highlightColor = Color.YELLOW.withAlpha(0.8);

                  const blinkInterval = setInterval(() => {
                    isOn = !isOn;

                    if (entity.polyline) {
                      entity.polyline.material = isOn ? highlightColor : originalPolylineMaterial;
                      entity.polyline.width = isOn ? 8 : originalWidth;
                    }

                    if (entity.polygon) {
                      entity.polygon.material = isOn ? highlightColor : originalMaterial;
                    }

                    if (entity.point) {
                      entity.point.color = isOn ? highlightColor : originalColor;
                      entity.point.pixelSize = isOn ? 20 : 10;
                    }
                  }, 400);

                  setTimeout(() => {
                    clearInterval(blinkInterval);

                    if (entity.polyline) {
                      entity.polyline.material = originalPolylineMaterial;
                      entity.polyline.width = originalWidth;
                    }

                    if (entity.polygon) entity.polygon.material = originalMaterial;
                    if (entity.point) {
                      entity.point.color = originalColor;
                      entity.point.pixelSize = 10;
                    }
                  }, 3000);
                }}
              >
                <LuFocus />
              </Button>

              {canEdit ? (
                <Button
                  size="sm"
                  p={0}
                  m={0}
                  variant="outline"
                  colorScheme="blue"
                  outline="none"
                  border="none"
                  onClick={() => {
                    const entity = item.entity;
                    const properties = entity.properties;
                    const featureProperties = {};

                    for (let key in properties) {
                      if (properties[key] && typeof properties[key].getValue === "function") {
                        const value = properties[key].getValue();
                        if (typeof value !== "object") featureProperties[key] = value;
                      }
                    }

                    setEditFeatureDialog({
                      open: true,
                      entity,
                      properties: featureProperties,
                    });
                  }}
                >
                  <LuPen />
                </Button>
              ) : null}

              {canEdit ? (
                <Button
                  p={0}
                  m={0}
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  outline="none"
                  border="none"
                  onClick={() => {
                    handleDeleteFeature(item, currentLayerForAttributeTable, setQueryTable, setSelectedFeatures, setCurrentLayerForAttributeTable, setMapObjects);
                  }}
                >
                  <LuTrash />
                </Button>
              ) : null}
            </HStack>
          </Table.Cell>

          <For each={cols}>
            {(c, i) => (
              <Table.Cell key={i} py="0" height={`${ROW_H}px`} whiteSpace="nowrap">
                {item[c]}
              </Table.Cell>
            )}
          </For>
        </Table.Row>
      );
    });

    return MemoRow;
  }, [
    ROW_H,
    canEdit,
    cols,
    currentLayerForAttributeTable,
    selectAllChecked,
    selectedSet,
    setCurrentLayerForAttributeTable,
    setEditFeatureDialog,
    setMapObjects,
    setQueryTable,
    setSelectedFeatures,
    viewerRef,
  ]);

  return (
    <Dialog.Root closeOnInteractOutside={false} modal={false} placement="top" motionPreset="slide-in-right" open={showQueryTableDialog} size="xl">
      <Portal>
        <Dialog.Positioner pointerEvents="none" maxH="80vh" overflow="hidden">
          <Dialog.Content mr="70px" mt="100px" minW="550px" w="fit-content" pointerEvents="all" pb={2} maxH="70vh" overflow="hidden" display="flex" flexDirection="column">
            <Dialog.Header>
              <VStack gap={0}>
                <Dialog.Title textAlign="left" w="full">
                  Attribute Table
                </Dialog.Title>
                <Text textAlign="left" w="full">
                  Layer: {removeFileExtension(currentLayerForAttributeTable?.file_name)}
                </Text>
              </VStack>
            </Dialog.Header>

            <Dialog.Body overflow="hidden" display="flex" flexDirection="column" flex="1" minH="0">
              <RadioGroup.Root
                mb={3}
                value={selectedVisibilityMode}
                onValueChange={(v) => {
                  setSelectedVisibilityMode(v.value);
                }}
              >
                <HStack gap="6">
                  {visibilityItems.map((item) => (
                    <RadioGroup.Item key={item.value} value={item.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </HStack>
              </RadioGroup.Root>

              <Group attached w="full">
                <Input
                  flex="1"
                  placeholder="Search ..."
                  value={filterSearch}
                  onChange={(value) => {
                    setFilterSearch(value.target.value);
                  }}
                />
                <Button
                  bg="black"
                  color="white"
                  variant="outline"
                  onClick={() => {
                    filterFeature(currentViewBounds);
                  }}
                >
                  Filter
                </Button>
              </Group>

              <Box display={selectedFeatures.length > 0 ? "block" : "none"} mt={3} mb={2}>
                <Button
                  bg="black"
                  color="white"
                  variant="outline"
                  onClick={() => {
                    setBultEditDialog(true);
                  }}
                >
                  Edit
                </Button>
              </Box>

              {queryTable.length !== 0 && (
                <Box ref={scrollRef} flex="1" minH="0" overflowY="scroll" css={{ scrollbarGutter: "stable" }} onScroll={onTableScroll}>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row height={`${ROW_H}px`}>
                        <Table.ColumnHeader textAlign="start" py="0" height={`${ROW_H}px`}>
                          {canEdit ? (
                            <Checkbox.Root
                              ml={1}
                              checked={selectAllChecked}
                              onCheckedChange={(checked) => {
                                const isChecked = checked.checked;
                                setSelectAllChecked(isChecked);

                                if (isChecked) {
                                  const allIds = queryTable.map((it) => it.id);
                                  setSelectedFeatures(allIds);
                                } else {
                                  setSelectedFeatures([]);
                                }
                              }}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control cursor="pointer" />
                            </Checkbox.Root>
                          ) : null}
                        </Table.ColumnHeader>

                        <For each={cols}>
                          {(item, index) => (
                            <Table.ColumnHeader key={index} py="0" height={`${ROW_H}px`} whiteSpace="nowrap">
                              {item}
                            </Table.ColumnHeader>
                          )}
                        </For>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {topPad > 0 ? (
                        <Table.Row height={`${topPad}px`}>
                          <Table.Cell colSpan={cols.length + 1} p={0} border="none" height={`${topPad}px`} />
                        </Table.Row>
                      ) : null}

                      {visibleRows.map((item) => (
                        <Row key={item.id} item={item} />
                      ))}

                      {bottomPad > 0 ? (
                        <Table.Row height={`${bottomPad}px`}>
                          <Table.Cell colSpan={cols.length + 1} p={0} border="none" height={`${bottomPad}px`} />
                        </Table.Row>
                      ) : null}
                    </Table.Body>
                  </Table.Root>
                </Box>
              )}
            </Dialog.Body>

            <Dialog.CloseTrigger asChild onClick={() => setShowQueryTableDialog(false)}>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default LayersAttributeTable;
